import type { HTMLAttributes, TdHTMLAttributes } from "react";
import { cn } from "../lib/utils";

type TableProps = HTMLAttributes<HTMLTableElement> & {
  ref?: React.Ref<HTMLTableElement>;
};
const Table = ({ className, ref, ...props }: TableProps) => (
  <div className="relative w-full overflow-auto rounded scroll-table h-full">
    <table
      ref={ref}
      className={cn("w-full bg-white caption-bottom text-sm", className)}
      {...props}
    />
  </div>
);
Table.displayName = "Table";

type TableHeaderProps = HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement>;
};
const TableHeader = ({ className, ref, ...props }: TableHeaderProps) => (
  <thead
    ref={ref}
    className={cn(
      "[&_tr]:border-b-0 [&_tr]:hover:bg-white truncate",
      className,
    )}
    {...props}
  />
);
TableHeader.displayName = "TableHeader";

type TableBodyProps = HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement>;
};
const TableBody = ({ className, ref, ...props }: TableBodyProps) => (
  <tbody ref={ref} className={className} {...props} />
);
TableBody.displayName = "TableBody";

type TableFooterProps = HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement>;
};
const TableFooter = ({ className, ref, ...props }: TableFooterProps) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className,
    )}
    {...props}
  />
);
TableFooter.displayName = "TableFooter";

type TableRowProps = HTMLAttributes<HTMLTableRowElement> & {
  ref?: React.Ref<HTMLTableRowElement>;
};
const TableRow = ({ className, ref, ...props }: TableRowProps) => (
  <tr
    ref={ref}
    className={cn("group h-8 transition-colors", className)}
    {...props}
  />
);
TableRow.displayName = "TableRow";

interface TableHeadProps extends HTMLAttributes<HTMLTableCellElement> {
  allSelected?: boolean;
}

type TableHeadComponentProps = TableHeadProps & {
  ref?: React.Ref<HTMLTableCellElement>;
};
const TableHead = ({
  className,
  allSelected = false,
  ref,
  ...props
}: TableHeadComponentProps) => (
  <th
    ref={ref}
    className={cn(
      "h-8 border-b border-gray-200 font-title text-body px-4 text-left align-middle [&:has([role=checkbox])]:px-3.5  transition-colors bg-white",
      "[&_button[role=checkbox]]:after:absolute [&_button[role=checkbox]]:after:content-[''] [&_button[role=checkbox]]:after:top-0 [&_button[role=checkbox]]:after:bottom-0 [&_button[role=checkbox]]:after:right-0 [&_button[role=checkbox]]:after:left-0",
      className,
    )}
    {...props}
  />
);
TableHead.displayName = "TableHead";

type TableCellProps = TdHTMLAttributes<HTMLTableCellElement> & {
  ref?: React.Ref<HTMLTableCellElement>;
};
const TableCell = ({ className, ref, ...props }: TableCellProps) => (
  <td
    ref={ref}
    className={cn(
      "px-4 py-0 h-full text-body border-gray-200 [&:has([role=checkbox])]:px-3.5 transition-colors",
      className,
    )}
    {...props}
  />
);

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
};
